import _ from 'lodash'

export default function ({
  model,
  setModel,
  commitModel,
  template,
  setTemplate,
  commitTemplate,
  classData,
  setClassData,
  commitClassData,
  objectData,
  setObjectData,
  commitObjectData,
  disableExpiredGooglePasses,
  setDisableExpiredGooglePasses,
  googlePassType
}) {

  const resolveApplePath = field => typeof field === "string" ? field : field?.applePath
  const resolveGooglePath = field => typeof field === "string" ? field : field?.googlePath

  const setOrganizationName = (field, value, options = {}) => {
    setModelValue(field, value, options)
    if (googlePassType !== "generic") setClassValue(field, value, options)
  }

  const setCardTitle = (field, value, options = {}) => {
    setTemplateValue(field, value, options)
    setObjectValue(field, value, options)
  }

  const setSecurityAnimation = (field, value, options = {}) => {
    if (value) setClassValue(field, "FOIL_SHIMMER", options)
    else deleteClassField(field, options)
  }

  const setSharing = (field, value, options = {}) => {
    setClassValue(field, value, options)
    setTemplateValue("meta.sharingProhibited", value !== "MULTIPLE_HOLDERS", options)
  }

  const setEventName = (field, value, options = {}) => {
    setClassValue(field, value, options)
    setTemplateValue(field, value, options)
  }

  const setEventVenueName = (field, value, options = {}) => {
    setClassValue(field, value, options)
  }

  const setEventVenueAddress = (field, value, options = {}) => {
    setClassValue(field, value, options)
  }

  const setProgramName = (field, value, options = {}) => {
    setClassValue(field, value, options)
    setTemplateValue(field, value, options)
  }

  const setOfferTitle = (field, value, options = {}) => {
    setClassValue(field, value, options)
    setTemplateValue(field, value, options)
  }

  const setScreenshots = (field, value, options = {}) => {
    const valueLookup = value ? "ELIGIBLE" : "INELIGIBLE"
    setClassValue(field, valueLookup, options)
  }

  const setHeader = (field, value, options = {}) => {
    if (value?.length) setObjectValue(field, value, options)
    else deleteObjectField(field, options)
    setTemplateValue(field, value, options)
  }

  const setSubHeader = (field, value, options = {}) => {
    if (value?.length) setObjectValue(field, value, options)
    else deleteObjectField(field, options)
    setTemplateValue(field, value, options)
  }

  const setModelValue = (field, value, options = {}) => {
    const path = resolveApplePath(field)
    if (!path) return

    const updater = prev => {
      const source = prev ?? {}
      const next = _.cloneDeep(source)
      _.set(next, path, value)
      return next
    }

    if (setModel) setModel(updater)
    if (options.commit && commitModel) commitModel(updater)
  }

  const setTemplateValue = (field, value, options = {}) => {
    const path = resolveApplePath(field)
    if (!path) return

    const updater = prev => {
      const source = prev ?? {}
      const next = _.cloneDeep(source)
      _.set(next, path, value)
      return next
    }

    if (setTemplate) setTemplate(updater)
    if (options.commit && commitTemplate) commitTemplate(updater)
  }

  const setClassValue = (field, value, options = {}) => {
    const path = resolveGooglePath(field)
    if (!path) return

    const updater = prev => {
      const source = prev ?? {}
      const next = _.cloneDeep(source)
      _.set(next, path, value)
      return next
    }

    if (setClassData) setClassData(updater)
    if (options.commit && commitClassData) commitClassData(updater)
  }

  const setObjectValue = (field, value, options = {}) => {
    const path = resolveGooglePath(field)
    if (!path) return

    const updater = prev => {
      const source = prev ?? {}
      const next = _.cloneDeep(source)
      _.set(next, path, value)
      return next
    }

    if (setObjectData) setObjectData(updater)
    if (options.commit && commitObjectData) commitObjectData(updater)
  }

  const deleteClassField = (field, options = {}) => {
    const path = resolveGooglePath(field)
    if (!path) return

    const updater = prev => {
      const source = prev ?? {}
      const next = _.cloneDeep(source)
      _.unset(next, path)
      return next
    }

    if (setClassData) setClassData(updater)
    if (options.commit && commitClassData) commitClassData(updater)
  }

  const deleteObjectField = (field, options = {}) => {
    const path = resolveGooglePath(field)
    if (!path) return

    const updater = prev => {
      const source = prev ?? {}
      const next = _.cloneDeep(source)
      _.unset(next, path)
      return next
    }

    if (setObjectData) setObjectData(updater)
    if (options.commit && commitObjectData) commitObjectData(updater)
  }

  const fieldDefinitions = {
    generic: [{
      name: "Header",
      help: "Main text below the logo",
      googlePath: "header.defaultValue.value",
      googleSource: objectData,
      applePath:"primaryFields[0].value",
      appleSource : template,
      optional : true,
      type: "text",
      length: 20,
      value : _.get(objectData,"header.defaultValue.value"),
      setValue: setHeader
    },
    {
      name: "Sub-Header",
      help: "Smaller text below the logo",
      googlePath: "subheader.defaultValue.value",
      googleSource: objectData,
      applePath:"primaryFields[0].label",
      appleSource : template,
      optional : true,
      type: "text",
      length: 20,
      value : _.get(objectData,"subheader.defaultValue.value") ?? "",
      setValue: setSubHeader
    }],

    eventTicket: [{
      name: "Event Name",
      help: "Name of the event.",
      googlePath: "eventName.defaultValue.value",
      googleSource: classData,
      applePath:"primaryFields[0].value",
      appleSource : template,
      type: "text",
      length: 20,
      value : _.get(classData,"eventName.defaultValue.value"),
      setValue: setEventName
    },
    {
      name: "Venue name",
      help: "Name of the event venue",
      googlePath: "venue.name.defaultValue.value",
      googleSource: classData,
      applePath:"secondaryFields[0].value",
      appleSource : template,
      optional : false,
      type: "text",
      length: 20,
      value : _.get(classData,"venue.name.defaultValue.value") ?? "",
      setValue: setEventVenueName
    },
    {
      name: "Venue Address",
      help: "Address of the event venue",
      googlePath: "venue.address.defaultValue.value",
      googleSource: classData,
      applePath:"secondaryFields[0].value",
      appleSource : template,
      optional : false,
      type: "text",
      length: 20,
      value : _.get(classData,"venue.address.defaultValue.value") ?? "",
      setValue: setEventVenueAddress
    },
    {
      name: "Event ID",
      help: "A unique ID for this event. This is used to group tickets together. Use the provided unique ID or provide your own.",
      googlePath: "eventId",
      googleSource: classData,
      type: "text",
      length: 30,
      value : _.get(classData,"eventId"),
      setValue: setClassValue
    }],

    giftCard: [],

    loyalty: [{
      name: "Program Name",
      help: "Name of the loyalty program. eg. 'Supermarket saving club'",
      googlePath: "programName",
      googleSource: classData,
      applePath:"primaryFields[0].value",
      appleSource : template,
      type: "text",
      length: 20,
      value : _.get(classData,"programName"),
      setValue: setProgramName
    }],

    offer: [{
      name: "Offer Provider",
      help: "Name of merchant providing the offer. Usually this will be your own name unless your are creating passes for a client.",
      googlePath: "provider",
      googleSource: classData,
      type: "text",
      length: 12,
      value : _.get(classData,"provider"),
      setValue: setClassValue
    },
    {
      name: "Offer title",
      help: "Title of the offer. eg. '10% off jeans this weekend'",
      googlePath: "title",
      googleSource: classData,
      applePath:"secondaryFields[0].value",
      appleSource : template,
      type: "text",
      length: 60,
      value : _.get(classData,"title"),
      setValue: setOfferTitle
    },
    {
      name: "Redemption Channel",
      help: "Where the offer can be redeemed (online, instore or both)",
      googlePath: "redemptionChannel",
      googleSource: classData,
      type: "radio",
      options: [
        { label: "Online", value: "ONLINE" },
        { label: "In Store", value: "INSTORE" },
        { label: "Online and in store", value: "BOTH" },
      ],
      value : _.get(classData,"redemptionChannel"),
      setValue: setClassValue
    }
    ],
  }

  const commonFieldsTop = [
    {
      name: "Organisation / Issuer name",
      help: "Name of the company issuing the wallet pass. Usually this will be your own name unless your are creating passes for a client.",
      applePath: "organizationName",
      appleSource: model,
      googlePath: "issuerName",
      googleSource: classData,
      type: "text",
      length: 20,
      value : _.get(model,"organizationName"),
      setValue: setOrganizationName
    },
    {
      name: "Card title",
      help: "Card title. Required, but can be set to a single space. Usually not shown if you use a wide logo.",
      applePath: "meta.title",
      appleSource: template,
      googlePath: "cardTitle.defaultValue.value",
      googleSource: objectData,
      type: "text",
      length: 20,
      value : _.get(template,"meta.title"),
      setValue: setCardTitle
    },
    {
      name: "Description (Apple)",
      help: "Short description of pass. Used by assistive assistant.",
      applePath: "meta.description",
      appleSource: template,
      type: "text",
      length: 20,
      value : _.get(template,"meta.description"),
      setValue: setTemplateValue
    }
  ]

  const commonFieldsBottom = [
    {
      name: "Sharing options",
      help: "Google: Allow passholder to share their pass with other people. Apple: Show/hide share button",
      googlePath: "multipleDevicesAndHoldersAllowedStatus",
      googleSource: classData,
      type: "radio",
      options: [
        { label: "Multiple holders (Google). Allow sharing (Apple)", value: "MULTIPLE_HOLDERS" },
        { label: "One user on all their devices (Google). No sharing (Apple)", value: "ONE_USER_ALL_DEVICES" },
        { label: "One user on a single device (Google). No sharing (Apple)", value: "ONE_USER_ONE_DEVICE" },
      ],
      value : _.get(classData,"multipleDevicesAndHoldersAllowedStatus"),
      setValue: setSharing
    },
    {
      name: "Allow screenshots? (Google)",
      help: "Allow user to take screenshots of the wallet pass. (Applies only to Google)",
      googlePath: "passConstraints.screenshotEligibility",
      googleSource: classData,
      type: "switch",
      value : _.get(classData,"passConstraints.screenshotEligibility") === "ELIGIBLE",
      setValue: setScreenshots
    },
    {
      name: "Show security animation? (Google)",
      help: "Shows a colour changing aniumation around the barcode",
      googlePath: "securityAnimation",
      googleSource: classData,
      type: "switch",
      value : _.get(classData,"securityAnimation") === "FOIL_SHIMMER",
      setValue: setSecurityAnimation
    },
    /*
    {
      name: "Disable expired google passes? (Google)",
      help: "Apple automatically disables the barcode when passes expires. Google does not. Smartix can push an update to a pass upon its expiry manually disabling the barcode. NOTE: There may be an additional charge for this.",
      googlePath: null,
      googleSource: disableExpiredGooglePasses,
      type: "switch",
      value : disableExpiredGooglePasses,
      setValue: setDisableExpiredGooglePasses
    }
      */
  ]

  return googlePassType ? commonFieldsTop.concat(fieldDefinitions[googlePassType]).concat(commonFieldsBottom) : []
}
